/**
 * How wide a column is drawn, shared by the charts that put a current and a proposal bar side by side.
 *
 * Source is encoded by width: the proposal bar is drawn narrower than the current one, so the pair reads as
 * one portfolio seen twice without spending a second colour on it. The base width steps down as the
 * navigator's window widens, since a slot that holds ten columns cannot hold two hundred at the same size.
 */

export type PointWidthParams = {
	/** Portfolios inside the navigator's current window, not the length of the data. */
	visibleCount: number;
	/** `main` is the current bar, `secondary` the proposal one. */
	metricType: "main" | "secondary";
	metricState: "null" | "zero" | "normal";
	/** Whether a proposal bar shares the slot, which narrows the current bar to make room. */
	hasSecondaryMetric?: boolean;
};

export function calculatePointWidth({
	visibleCount,
	metricType,
	metricState,
	hasSecondaryMetric,
}: PointWidthParams): number {
	let baseWidth: number;
	if (visibleCount <= 10) {
		baseWidth = 32;
	} else if (visibleCount <= 30) {
		baseWidth = 24;
	} else if (visibleCount <= 40) {
		baseWidth = 16;
	} else if (visibleCount <= 60) {
		baseWidth = 14;
	} else if (visibleCount <= 100) {
		baseWidth = 12;
	} else if (visibleCount <= 200) {
		baseWidth = 10;
	} else {
		baseWidth = 8;
	}

	if (metricType === "secondary") {
		return Math.floor(baseWidth * 0.3);
	}

	if (metricState === "null" || metricState === "zero") {
		return Math.floor(baseWidth * 0.8);
	}

	if (hasSecondaryMetric) {
		return Math.floor(baseWidth * 0.7);
	}

	return baseWidth;
}
